
import { useState, useRef } from 'react';
import { motion } from 'framer-motion';

interface Project {
  id: number;
  title: string;
  category: string;
  description: string;
  year: string;
  client: string;
  weight: string;
  image: string;
  featured: boolean;
}

interface ProjectCardProps {
  project: Project;
  index: number;
}

export default function ProjectCard({ project, index }: ProjectCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;
    const rY = (mouseX / (rect.width / 2)) * 8;
    const rX = -(mouseY / (rect.height / 2)) * 8;
    setRotateX(rX);
    setRotateY(rY);
  };

  const handleMouseLeave = () => {
    setRotateX(0);
    setRotateY(0);
    setIsHovered(false);
  };

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 60, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.7,
        delay: index * 0.08,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      viewport={{ once: true, margin: '-50px' }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      className="group cursor-pointer"
      style={{ perspective: '1200px' }}
    >
      <div
        className="relative overflow-hidden bg-[#111111] border border-[#C0C7CF]/10 transition-all duration-500"
        style={{
          transform: `rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(${isHovered ? '20px' : '0px'})`,
          transformStyle: 'preserve-3d',
          transition:
            'transform 0.15s ease-out, box-shadow 0.4s ease-out, border-color 0.4s ease-out',
          boxShadow: isHovered
            ? '0 25px 60px rgba(0,0,0,0.6), 0 0 30px rgba(0,229,255,0.1), inset 0 1px 0 rgba(0,229,255,0.1)'
            : '0 4px 20px rgba(0,0,0,0.3)',
          borderColor: isHovered
            ? 'rgba(0,229,255,0.4)'
            : 'rgba(192,199,207,0.1)',
        }}
      >
        <div className="relative w-full h-[220px] sm:h-[280px] lg:h-[320px] overflow-hidden">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover object-top transition-transform duration-700 ease-out"
            style={{ transform: isHovered ? 'scale(1.08)' : 'scale(1)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/30 to-transparent"></div>

          <div
            className="absolute inset-0 bg-[#00E5FF]/5 transition-opacity duration-500"
            style={{ opacity: isHovered ? 1 : 0 }}
          ></div>

          <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
            <span
              className="inline-block px-2.5 sm:px-4 py-1 sm:py-1.5 bg-[#050505]/80 backdrop-blur-md border border-[#00E5FF]/30 text-[#00E5FF] text-[8px] sm:text-[10px] tracking-[0.2em] uppercase font-semibold"
              style={{ fontFamily: 'Orbitron, sans-serif' }}
            >
              {project.category}
            </span>
          </div>

          <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
            <span
              className="inline-block px-2 sm:px-3 py-1 sm:py-1.5 bg-[#050505]/80 backdrop-blur-md text-[#C0C7CF] text-[8px] sm:text-[10px] tracking-wider font-medium"
              style={{ fontFamily: 'Orbitron, sans-serif' }}
            >
              {project.year}
            </span>
          </div>

          {project.featured && (
            <div className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4">
              <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-[#00E5FF] text-[#050505]">
                <i className="ri-star-fill text-xs sm:text-sm"></i>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6 relative" style={{ transform: 'translateZ(30px)' }}>
          <h3
            className="text-base sm:text-lg font-bold text-white mb-1.5 sm:mb-2 group-hover:text-[#00E5FF] transition-colors duration-300 leading-tight"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            {project.title}
          </h3>

          <p
            className="text-[#C0C7CF]/80 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-5 line-clamp-2"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {project.description}
          </p>

          <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-[#C0C7CF]/10">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-1 sm:gap-1.5">
                <i className="ri-building-2-line text-[#00E5FF] text-[10px] sm:text-xs"></i>
                <span
                  className="text-[#C0C7CF]/60 text-[9px] sm:text-[11px]"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {project.client}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-1.5">
              <i className="ri-weight-line text-[#00E5FF] text-[10px] sm:text-xs"></i>
              <span
                className="text-[#C0C7CF]/60 text-[9px] sm:text-[11px]"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {project.weight}
              </span>
            </div>
          </div>

          <div
            className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center border border-[#00E5FF]/30 text-[#00E5FF] transition-all duration-300 opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0"
          >
            <i className="ri-arrow-right-up-line text-xs sm:text-sm"></i>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
